import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { InfoBlock, InfoBlockRounded, InfoBlockType } from 'cng-common/components/infoBlock'
import { SimpleTable } from 'cng-common/components/simpleTable'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { ModalDialog } from 'core-components/library/modalDialog'
import type { IModalContent } from 'core-components/library/modalDialog/models/IModalContent'
import { getMetricModeTranslationKey } from 'partner360/entities/financialMetricMode'

import { buildTnetReviewMetrics } from '../lib/buildTnetReviewMetrics'
import { formatCurrency, formatPercentChange } from '../lib/formatters'
import type { TnetReviewModalProps, TnetReviewPartner } from '../model/types'
import { useConfirmTnetReview } from '../model/useConfirmTnetReview'
import { tnetReviewModalTranslation } from '../translation'

import styles from './TnetReviewModal.module.scss'

type TnetReviewModalContentProps = IModalContent & Omit<TnetReviewModalProps, 'show'>

type GrowthTone = 'positive' | 'negative' | 'neutral'

const formatAmount = (value: number | null) => (value === null ? '—' : formatCurrency(value))

const formatGrowth = (value: number | null) => (value === null ? '—' : formatPercentChange(value))

const getGrowthTone = (growthValue: number | null): GrowthTone =>
  growthValue === null ? 'neutral' : growthValue < 0 ? 'negative' : 'positive'

const getGrowthIcon = (growthTone: GrowthTone) =>
  growthTone === 'negative' ? '↘' : growthTone === 'neutral' ? '→' : '↗'

const PartnerSummary = ({ region, country, partnerName, pid, locationId }: TnetReviewPartner) => (
  <div className={styles.partnerSummary}>
    <p className={styles.partnerRegion}>
      {region}, {country}
    </p>
    <p className={styles.partnerName}>{partnerName}</p>
    <p className={styles.partnerIds}>
      <Translation
        text={tnetReviewModalTranslation.partnerIds}
        replaceParams={[
          { key: '{pid}', value: pid },
          { key: '{locationId}', value: locationId },
        ]}
        span
      />
    </p>
  </div>
)

const TnetReviewModalContent = ({
  partner,
  category,
  finRows,
  metricMode,
  reviewMetricMode,
  currentFiscalYear,
  programType,
  onGoToPlanning,
  onConfirmed,
  onClose,
}: TnetReviewModalContentProps) => {
  const { t } = useTranslation()
  const { confirmReview, isConfirmingReview, hasConfirmReviewError } = useConfirmTnetReview()
  const reviewMetrics = useMemo(
    () => buildTnetReviewMetrics(finRows, metricMode, reviewMetricMode),
    [finRows, metricMode, reviewMetricMode]
  )

  const handleConfirmReview = async () => {
    if (!partner) {
      return
    }

    const isConfirmed = await confirmReview({
      partnerId: partner.pid,
      fYear: currentFiscalYear,
      programType,
      category,
      reviewMetricMode,
    })

    if (!isConfirmed) {
      return
    }

    onClose()
    await onConfirmed?.()
  }

  const currentFiscalYearLabel = String(currentFiscalYear).slice(-2)
  const previousFiscalYearLabel = String(currentFiscalYear - 1).slice(-2)
  const planningMetricLabel = t(getMetricModeTranslationKey(metricMode))
  const reviewMetricLabel = t(getMetricModeTranslationKey(reviewMetricMode))
  const reviewMetricReplaceParams = [{ key: '{metric}', value: reviewMetricLabel }]
  const shouldShowPlanningRow = metricMode !== reviewMetricMode
  const summaryGrowth = formatGrowth(reviewMetrics.summaryGrowth)
  const summaryTone = getGrowthTone(reviewMetrics.summaryGrowth)
  const summaryTitleKey =
    summaryTone === 'neutral'
      ? tnetReviewModalTranslation.summaryNeutral
      : summaryTone === 'negative'
        ? tnetReviewModalTranslation.summaryNegative
        : tnetReviewModalTranslation.summaryPositive
  const summaryIcon = getGrowthIcon(summaryTone)

  const comparisonRows = [
    ...(shouldShowPlanningRow
      ? [
          {
            id: 'planning',
            isReviewRow: false,
            metricLabel: planningMetricLabel,
            previousH2Actuals: formatAmount(reviewMetrics.planning.previousH2Actuals),
            h1Actuals: formatAmount(reviewMetrics.planning.h1Actuals),
            h1Growth: formatGrowth(reviewMetrics.planning.h1YoYGrowth),
            h1GrowthValue: reviewMetrics.planning.h1YoYGrowth,
            h2Plan: formatAmount(reviewMetrics.planning.h2Plan),
            h2Growth: formatGrowth(reviewMetrics.planning.h2YoYGrowth),
            h2GrowthValue: reviewMetrics.planning.h2YoYGrowth,
          },
        ]
      : []),
    {
      id: 'review',
      isReviewRow: true,
      metricLabel: reviewMetricLabel,
      previousH2Actuals: formatAmount(reviewMetrics.review.previousH2Actuals),
      h1Actuals: formatAmount(reviewMetrics.review.h1Actuals),
      h1Growth: formatGrowth(reviewMetrics.review.h1YoYGrowth),
      h1GrowthValue: reviewMetrics.review.h1YoYGrowth,
      h2Plan: formatAmount(reviewMetrics.review.h2Plan),
      h2Growth: formatGrowth(reviewMetrics.review.h2YoYGrowth),
      h2GrowthValue: reviewMetrics.review.h2YoYGrowth,
    },
  ].map((row) => ({
    ...row,
    h1GrowthTone: getGrowthTone(row.h1GrowthValue),
    h1GrowthIcon: getGrowthIcon(getGrowthTone(row.h1GrowthValue)),
    h2GrowthTone: getGrowthTone(row.h2GrowthValue),
    h2GrowthIcon: getGrowthIcon(getGrowthTone(row.h2GrowthValue)),
  }))

  if (!partner) {
    return <></>
  }

  return (
    <div className={styles.root}>
      {reviewMetricMode === 'tnet' && (
        <InfoBlock
          text={tnetReviewModalTranslation.helperText}
          type={InfoBlockType.Help}
          rounded={InfoBlockRounded.Small}
        />
      )}
      <div className={`${styles.summaryPanel} ${styles[`summaryPanel${summaryTone}`]}`}>
        <div className={styles.summaryIntro}>
          <PartnerSummary {...partner} />
        </div>

        <div className={styles.summaryMain}>
          <div className={`${styles.summaryIcon} ${styles[`summaryIcon${summaryTone}`]}`}>
            {summaryIcon}
          </div>
          <div className={styles.summaryCopy}>
            <strong className={styles.summaryValue}>{summaryGrowth}</strong>
            <span className={styles.summaryDescription}>
              <Translation text={summaryTitleKey} span />
            </span>
          </div>
        </div>
      </div>

      {hasConfirmReviewError && (
        <div className={styles.errorMessage}>
          <Translation text={tnetReviewModalTranslation.confirmReviewError} span />
        </div>
      )}

      <div className={styles.tableShell}>
        <SimpleTable className={styles.table}>
          <SimpleTable.HeaderRow className={styles.tableHeaderRow}>
            <SimpleTable.HeaderCell className={styles.metricHeaderCell}>
              <Translation text={tnetReviewModalTranslation.metricColumn} span />
            </SimpleTable.HeaderCell>
            <SimpleTable.HeaderCell className={styles.tableHeaderCell}>
              <Translation
                text={tnetReviewModalTranslation.actualsColumn}
                replaceParams={[{ key: '{fy}', value: previousFiscalYearLabel }]}
                span
              />
            </SimpleTable.HeaderCell>
            <SimpleTable.HeaderCell className={styles.tableHeaderCell}>
              <Translation
                text={tnetReviewModalTranslation.h1ActualsColumn}
                replaceParams={[{ key: '{fy}', value: currentFiscalYearLabel }]}
                span
              />
            </SimpleTable.HeaderCell>
            <SimpleTable.HeaderCell className={styles.tableHeaderCell}>
              <Translation
                text={tnetReviewModalTranslation.h1GrowthColumn}
                replaceParams={[{ key: '{fy}', value: currentFiscalYearLabel }]}
                span
              />
            </SimpleTable.HeaderCell>
            <SimpleTable.HeaderCell className={styles.tableHeaderCell}>
              <Translation
                text={tnetReviewModalTranslation.h2PlanColumn}
                replaceParams={[{ key: '{fy}', value: currentFiscalYearLabel }]}
                span
              />
            </SimpleTable.HeaderCell>
            <SimpleTable.HeaderCell className={styles.tableHeaderCell}>
              <Translation
                text={tnetReviewModalTranslation.h2GrowthColumn}
                replaceParams={[{ key: '{fy}', value: currentFiscalYearLabel }]}
                span
              />
            </SimpleTable.HeaderCell>
          </SimpleTable.HeaderRow>

          {comparisonRows.map((row) => (
            <SimpleTable.BodyRow
              key={row.id}
              className={row.isReviewRow ? styles.secondaryRow : styles.dataRow}
            >
              <SimpleTable.HeaderCell className={styles.metricCell}>
                <span>{row.metricLabel}</span>
              </SimpleTable.HeaderCell>
              <SimpleTable.BodyCell className={styles.valueCell}>
                {row.previousH2Actuals}
              </SimpleTable.BodyCell>
              <SimpleTable.BodyCell className={styles.valueCell}>
                {row.h1Actuals}
              </SimpleTable.BodyCell>
              <SimpleTable.BodyCell
                className={`${styles.valueCell} ${
                  row.h1GrowthValue === null
                    ? styles.placeholderValue
                    : row.h1GrowthTone === 'negative'
                      ? styles.negativeValue
                      : styles.positiveValue
                }`}
              >
                <span className={styles.growthContent}>
                  <span
                    className={`${styles.growthIcon} ${styles[`growthIcon${row.h1GrowthTone}`]}`}
                  >
                    {row.h1GrowthIcon}
                  </span>
                  <span>{row.h1Growth}</span>
                </span>
              </SimpleTable.BodyCell>
              <SimpleTable.BodyCell className={styles.valueCell}>{row.h2Plan}</SimpleTable.BodyCell>
              <SimpleTable.BodyCell
                className={`${styles.valueCell} ${
                  row.h2GrowthValue === null
                    ? styles.placeholderValue
                    : row.h2GrowthTone === 'negative'
                      ? styles.negativeValue
                      : styles.positiveValue
                }`}
              >
                <span className={styles.growthContent}>
                  <span
                    className={`${styles.growthIcon} ${styles[`growthIcon${row.h2GrowthTone}`]}`}
                  >
                    {row.h2GrowthIcon}
                  </span>
                  <span>{row.h2Growth}</span>
                </span>
              </SimpleTable.BodyCell>
            </SimpleTable.BodyRow>
          ))}
        </SimpleTable>
      </div>

      <div className={styles.actions}>
        <Button
          type="button"
          color="danger"
          variant="text"
          size="sm"
          className={styles.actionButton}
          disabled={isConfirmingReview}
          onClick={onGoToPlanning}
        >
          {reviewMetricMode === 'tnet' ? (
            <Translation text={tnetReviewModalTranslation.goToPlanning} span />
          ) : (
            <Translation
              text={tnetReviewModalTranslation.goToPlanningWithMetric}
              replaceParams={reviewMetricReplaceParams}
              span
            />
          )}
        </Button>
        <Button
          type="button"
          size="sm"
          className={styles.actionButton}
          icon="kbase kbase-check"
          loading={isConfirmingReview}
          disabled={isConfirmingReview}
          onClick={handleConfirmReview}
        >
          {reviewMetricMode === 'tnet' ? (
            <Translation text={tnetReviewModalTranslation.confirmReview} span />
          ) : (
            <Translation
              text={tnetReviewModalTranslation.confirmReviewWithMetric}
              replaceParams={reviewMetricReplaceParams}
              span
            />
          )}
        </Button>
      </div>
    </div>
  )
}

export const TnetReviewModal = ({
  show,
  partner,
  category,
  finRows,
  metricMode,
  reviewMetricMode,
  currentFiscalYear,
  programType,
  onClose,
  onGoToPlanning,
  onConfirmed,
}: TnetReviewModalProps) => {
  const { t } = useTranslation()
  const reviewMetricLabel = t(getMetricModeTranslationKey(reviewMetricMode))
  const reviewMetricReplaceParams = [{ key: '{metric}', value: reviewMetricLabel }]

  return (
    <ModalDialog
      show={show}
      size="lg"
      classes={{ window: styles.window }}
      content={TnetReviewModalContent}
      header={() => (
        <div className={styles.title}>
          {reviewMetricMode === 'tnet' ? (
            <>
              <Translation text={tnetReviewModalTranslation.titleWithTnet} span />
              {category ? (
                <>
                  {' '}
                  <Translation text={category} span />
                </>
              ) : null}
            </>
          ) : (
            <>
              <Translation
                text={tnetReviewModalTranslation.titleWithoutTnet}
                replaceParams={reviewMetricReplaceParams}
                span
              />
              {category ? (
                <>
                  {' '}
                  <Translation text={category} span />
                </>
              ) : null}
            </>
          )}
        </div>
      )}
      contentProps={{
        partner,
        category,
        finRows,
        metricMode,
        reviewMetricMode,
        currentFiscalYear,
        programType,
        onGoToPlanning,
        onConfirmed,
        onClose,
      }}
      onClose={onClose}
    />
  )
}
